import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";

import _ from 'lodash';
import cx from 'classnames';
import shortid from 'shortid';
import { components } from 'react-select';
import { Property } from '../properties';
import { ANCHOR, CAROUSEL, HOTSPOT, PAGE, SLIDE } from "../../../../constants/widgetTypes";
import { fetchAssetMappings, invalidateAssetMappings } from 'actions/content';
import { queryPages, invalidatePageQuery, fetchPage, fetchAnchorWidgets } from 'actions/pages';

import {
  Accordion,
  useAccordionButton,
  AccordionContext,
  Button,
  Form,
  Dropdown
} from 'react-bootstrap';

import { IKImage } from "imagekitio-react";
import dateformat from "dateformat";
import Tooltip from 'components/Tooltip';
import Truncate from "react-truncate";
import FileService from 'services/file.service';
import { getWidgetType } from 'components/Editor/helper/widget';
import StoryboardContext from 'components/Storyboard/context';
import InfiniteScroll from 'components/InfiniteScroll';
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';
import { createMixpanelEvent } from 'actions/mixpanel';
import { MIXPANEL_ONCLICK_GATING_ENABLED, MIXPANEL_PAGE_ACTION_ENABLED } from 'constants/mixpanelEvents';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import { INTERACTIVE_CONTENT_TYPES, MULTIPAGE_STORYBOARDS, SLIDE_STORYBOARDS, STORYBOARD_TYPES } from 'constants/storyboardType';

import styles from './actions.module.scss';

const Actions = ({ target, editorSession, value, onChange, onCancel }) => {
  const dispatch = useDispatch();
  const storyboardContext = React.useContext(StoryboardContext);
  const [state, setState] = useState({
    action: getValue(value, storyboardContext.type),
    error: null
  });

  const editor = useSelector(state => _.get(state, `${editorSession}.present`) || null);
  const actionType = _.get(state.action, 'type');
  const actionValue = _.get(state.action, 'value') || null;
  const anchorWidgets = useSelector(state => _.get(state, 'pages.anchorWidgets') || {});

  useEffect(() => {
    const nextAction = normalizePageActionForEditor(value);
    setState((prev) => {
      if (_.isEqual(prev.action, nextAction)) {
        return prev;
      }
      return {
        ...prev,
        action: nextAction,
      };
    });
  }, [value]);

  useEffect(() => {
    if (storyboardContext.type === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2 && editor.entityType !== 'GATING') {
      dispatch(fetchAnchorWidgets({ id: storyboardContext.id }))
    }
  }, []);

  const handleTypeChange = (type) => {
    var action = null;
    if (type !== actionType) {
      action = {
        uid: `action_${shortid()}`,
        type: type,
        newTab: true
      };
    }

    handleActionChange(action);
  }

  const handleActionChange = (value) => {
    if (value) {
      setState(state => {
        return {
          ...state,
          action: value.type === 'none' ? null : value,
          error: null
        }
      });

      if((value.type === 'slide' || value.type === 'carousel' || value.type === 'page') && value.deleted) {
        onChange(null);
        setState(state => {
          return {
            ...state,
            action: null,
            error: null
          }
        });
      }
    }
  }

  const handleCancel = () => {
    setState(state => {
      return {
        ...state,
        error: null
      }
    });
    onCancel();
  }

  const handleSave = () => {
    if (actionType === "carousel") {
      const value = _.get(state.action, 'value') || {};

      if(!value.carouselUid || !value.slideUid) {
        setState(state => {
          return {
            ...state,
            error: "field should not be empty !"
          }
        });

        return;
      }
    }

    if (actionType === "slide") {
      const value = _.get(state.action, 'value') || {};

      if(!value.page || !value.slide) {
        setState(state => {
          return {
            ...state,
            error: "field should not be empty !"
          }
        });

        return;
      }
    }

    if (actionType == "gating") {
      const gating = _.get(state.action, 'value.gating');
      if (!gating) {
        setState(state => {
          return {
            ...state,
            error: "field should not be empty !"
          }
        });

        return;
      }
    }

    if (actionType != "pageAnchor") {
      if ((state.action && !state.action.value) && actionType != 'none' || (state.action && state.action.value == true)) {
        setState(state => {
          return {
            ...state,
            error: "field should not be empty !"
          }
        });

        return;
      }
    }

    setState(state => {
      return {
        ...state,
        error: null
      }
    });

    onChange(normalizePageActionForEditor(state.action));
    onCancel();

    if (actionType == "page") {
      const mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type)
      dispatch(createMixpanelEvent(MIXPANEL_PAGE_ACTION_ENABLED, mixpanelPayload))
    }

    if (actionType == "gating") {
      const mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type)
      dispatch(createMixpanelEvent(MIXPANEL_ONCLICK_GATING_ENABLED, mixpanelPayload))
    }
  }

  const handleSaveError = () => {
    setState(state => {
      return {
        ...state,
        error: null
      }
    });
  }

  const toggleOptional = (event) => {
    const value = _.get(state.action, 'value');

    const updateOptional = {
      ...state.action,
      value: {
        ...value,
        optional: !value.optional
      }
    }

    handleActionChange(updateOptional);
  }

  return (
    <Property.Dialog
      width={250}
      target={`#${target}`}
      title={"Add action"}
      onCancel={handleCancel}
    >
      <Accordion
        activeKey={actionType}
        defaultActiveKey="none"
        className={styles.actions}
      >
        <div className={styles.action}>
          <ActionHeader eventKey="none" onClick={handleTypeChange}>
            None
          </ActionHeader>
        </div>
        <div className={styles.action}>
          <ActionHeader eventKey="link" onClick={handleTypeChange}>
            Web Address
          </ActionHeader>
          <Accordion.Collapse eventKey="link">
            <LinkAction
              value={state.action}
              onChange={handleActionChange}
              saveError={state.error}
              handleSaveError={handleSaveError}
            />
          </Accordion.Collapse>
        </div>
        {SLIDE_STORYBOARDS.includes(storyboardContext.type) && (
          <div className={styles.action}>
            <ActionHeader eventKey="slide" onClick={handleTypeChange}>
              Slide
            </ActionHeader>
            <Accordion.Collapse eventKey="slide">
              <PageSpatialHyperLink
                value={state.action}
                onChange={handleActionChange}
                saveError={state.error}
                editorSession={editorSession}
              />
            </Accordion.Collapse>
          </div>
        )}
        {
          editor.entityType !== "GATING" && 
          editor.entityType !== "LIVE_ELEMENT" &&
          editor.entityType !== "PALETTE_COMPONENT" &&
          <div className={styles.action}>
            <ActionHeader eventKey="carousel" onClick={handleTypeChange}>
              Carousel
            </ActionHeader>
            <Accordion.Collapse eventKey="carousel">
              <PageCarouselLink
                value={state.action}
                onChange={handleActionChange}
                saveError={state.error}
                editorSession={editorSession}
                storyboardType={storyboardContext.type}
              />
            </Accordion.Collapse>
          </div>
        }
        {!SLIDE_STORYBOARDS.includes(storyboardContext.type) && (
          <div className={styles.action}>
            <ActionHeader eventKey="pageAnchor" onClick={handleTypeChange}>
              Top/Bottom to page
            </ActionHeader>
            <Accordion.Collapse eventKey="pageAnchor">
              <PageAnchorAction
                value={state.action}
                onChange={handleActionChange}
              />
            </Accordion.Collapse>
          </div>
        )}
        {!MULTIPAGE_STORYBOARDS.includes(storyboardContext.type) &&
          editor.entityType != "LIVE_ELEMENT" && (
            <div className={styles.action}>
              <ActionHeader eventKey="widgetAnchor" onClick={handleTypeChange}>
                Anchor
              </ActionHeader>
              <Accordion.Collapse eventKey="widgetAnchor">
                <AnchorAction
                  editorSession={editorSession}
                  value={state.action}
                  onChange={handleActionChange}
                  saveError={state.error}
                />
              </Accordion.Collapse>
            </div>
          )}
        {storyboardContext.type === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2 && editor.entityType != 'LIVE_ELEMENT' && 
          <div className={styles.action}>
            <ActionHeader eventKey="widgetAnchor" onClick={handleTypeChange}>
              Anchor
            </ActionHeader>
            <Accordion.Collapse eventKey="widgetAnchor">
              <MultipageAnchorAction
                editorSession={editorSession}
                state={state.action}
                onChange={handleActionChange}
                saveError={state.error}
                anchorWidgets={anchorWidgets}
              />
            </Accordion.Collapse>
          </div>
        }
        
        {editor.entityType !== "GATING" &&
          !SLIDE_STORYBOARDS.includes(storyboardContext.type) &&
          editor.entityType != "LIVE_ELEMENT" && (
            <>
              <div className={styles.action}>
                <ActionHeader eventKey="asset" onClick={handleTypeChange}>
                  Asset
                </ActionHeader>
                <Accordion.Collapse eventKey="asset">
                  <AssetAction
                    value={state.action}
                    onChange={handleActionChange}
                    saveError={state.error}
                  />
                </Accordion.Collapse>
              </div>

              {!INTERACTIVE_CONTENT_TYPES.includes(storyboardContext.type) && editor.entityType != 'LIVE_ELEMENT' && (
                  <div className={styles.action}>
                    <ActionHeader eventKey="page" onClick={handleTypeChange}>
                      Page
                    </ActionHeader>
                    <Accordion.Collapse eventKey="page">
                      <PageAction
                        value={state.action}
                        onChange={handleActionChange}
                        saveError={state.error}
                      />
                    </Accordion.Collapse>
                  </div>
                )}
            </>
          )}
        {!SLIDE_STORYBOARDS.includes(storyboardContext.type) &&
          editor.entityType !== "GATING" &&
          editor.entityType != "LIVE_ELEMENT" && (
            <div className={styles.action}>
              <div className={styles.radiowrapper}>
                <ActionHeader eventKey="gating" onClick={handleTypeChange}>
                  Gating
                </ActionHeader>
                {/* optional removed for button gating type */}
                {/* {actionType == "gating" && actionValue && (
                  <Form.Check
                    custom
                    type="checkbox"
                    label="Optional"
                    onMouseDown={toggleOptional}
                    checked={actionValue.optional}
                    className={styles.optionalCheck}
                  />
                )} */}
              </div>

              <Accordion.Collapse eventKey="gating">
                <GatingAction
                  editorSession={editorSession}
                  value={state.action}
                  onChange={handleActionChange}
                  saveError={state.error}
                />
              </Accordion.Collapse>
            </div>
          )}
      </Accordion>
      <div className={styles.buttonTray}>
        <Button className={styles.save} onClick={handleSave}>
          Save Changes
        </Button>
      </div>
    </Property.Dialog>
  );
}

const ActionHeader = ({ eventKey, onClick, children }) => {
  const { activeEventKey } = React.useContext(AccordionContext);

  const decoratedOnClick = useAccordionButton(
    eventKey,
    () => onClick && onClick(eventKey),
  );

  const active = activeEventKey === eventKey;

  return (
    <div className={cx(styles.header, { [styles.active]: active })} onClick={decoratedOnClick}>
      <input type="radio" defaultChecked={active}></input>
      <label>{children}</label>
    </div>
  );
}

const LinkAction = ({ value, onChange, saveError, handleSaveError }) => {
  const [error, setError] = useState(null);

  const url = _.get(value, 'value', '') === true ? "" : _.get(value, 'value', '');
  const newTab = _.get(value, 'newTab', true);

  const handleChange = (e) => {
    var url = e.target.value;
    handleSaveError()

    if (!url || url.trim().length === 0) {
      setError("Please enter valid url");
    } else {
      setError(null);
    }

    const action = {
      ...value,
      value: url.trim()
    }

    onChange(action);
  }

  const handleTargetChange = (target) => {
    const action = {
      ...value,
      newTab: target
    }

    onChange(action);
  }

  return (
    <div className={styles.linkAction}>
      <Property name='Paste Url below' className={styles.linkProperty}>
        <Property.Input value={url}
          onChange={handleChange}
          placeholder="Enter url"
          error={saveError ? saveError : error} />
      </Property>
      <Property name="Open in" className={styles.targetProperty}>
        <div className={styles.body}>
          <Property.RadioButton className={styles.targetRadio}
            title='Current'
            value={!newTab}
            onClick={() => handleTargetChange(false)}
          />
          <Property.RadioButton className={styles.targetRadio}
            title='New Window'
            value={newTab}
            onClick={() => handleTargetChange(true)}
          />
        </div>
      </Property>
    </div>
  )
}

const SpatialHyperLinkDropdownOption = (props) => {
  return (
    <components.Option {...props} className={styles.anchorOption}>
      {props.children}
    </components.Option>
  );
}

const PageSpatialHyperLink = ({
  value,
  onChange,
  saveError,
  editorSession
}) => {
  const pages = useSelector(state => {
    const pages = _.get(state, 'pages.items', []);
    return pages.map(item => {
      return {
        value: item.id,
        label: item.name,
        type: "page",
        page: item
      }
    })
  });

  const [items, setItems] = useState(pages);
  const [selectedPage, setSelectedPage] = useState(null);

  useEffect(() => {
    if (!_.isEqual(items, pages)) {
      setItems(pages);
    }
  }, [pages]);

  useEffect(() => {
    try {
    const page = _.get(value, 'value.page');
    const isPageExist = items.find(item => item.value === page);

    if(!items?.length) {
      return;
    }

    if((!isPageExist && page) || value?.value === null) {
      const action = _.omit(value, 'value');
      onChange({
        ...action,
        deleted: true
      });
      return;
    }

    if (!page) {
      setSelectedPage(null);
      return
    }

    setSelectedPage(page);
  } catch {};
  }, [value, items]);

  const handleChange = (e) => {
    if(e.value === selectedPage) return;

    const action = {
      ...value,
      value: {
        icon: null,
        slide: "",
        name: "",
        uid: "",
        page: e.value,
        pageName: e.page?.name || "",
      },
    };

    onChange(action);
  }

  return (
    <div className={styles.pageHyperLink}>
      <Property name='Page' className={styles.pageHyperLinkProperty}>
        <Property.Dropdown
          searchable={false}
          className={styles.pageHyperLinkDropOption}
          options={items}
          components={{
            Option: SpatialHyperLinkDropdownOption
          }}
          value={selectedPage}
          onChange={handleChange}
          error={(saveError && !selectedPage) ? saveError : null}
        />
      </Property>
      <Property name='Slide' className={styles.pageHyperLinkProperty}>
        <PageHyperLinkSlide
          value={value}
          editorSession={editorSession}
          onChange={onChange}
          saveError={saveError}
        />
      </Property>
    </div>
  )
}

const CustomSingleValue = (props) => {
  const [tooltip, setTooltip] = useState(false)
  
  return (
  <>
    <Tooltip placement="auto" content={props.data.label} container={document.body} disabled={!tooltip}>
      <span style={{ position: 'absolute' }}>
        <Truncate lines={1} width={170} ellipsis="..." onTruncate={(tooltip) => setTooltip(tooltip)}>
          <components.SingleValue {...props} />
        </Truncate>
      </span>
    </Tooltip>
  </>
  )
};

const CarouselLinkDropdown = (props) => {
  return (
    <components.Option {...props} className={styles.anchorOption}>
      {props.children}
    </components.Option>
  );
}

const PageCarouselLink = ({ value, onChange, saveError, editorSession, storyboardType }) => {
  const breakpoint = useSelector(
    (state) => _.get(state, `${editorSession}.present.breakpoint`) || "desktop"
  );
  let widgets = useSelector((state) => {
    return _.get(state, `${editorSession}.present.widgets`)  || [];
  });
  const activePageSection = useSelector(state => _.get(state, `${editorSession}.present.interactive.active`));
  const interactiveContentWidgets = useMemo(() => getWidgetsWithAncestor(widgets, activePageSection), [widgets, activePageSection]);
  
  const [selectedCarouselId, setSelectedCarouselId] = useState(null);
  
  const items = (() => {
    let source = [];

    if(storyboardType === 'INTERACTIVE_CONTENT') {
      source = interactiveContentWidgets;
    } else {
      source = _.orderBy(widgets, 'order', 'desc');
    }

    if (!Array.isArray(source)) return [];
  
    const filtered = source.filter(item => {
      if (item.type !== CAROUSEL) return false;
      
      const parentType = getWidgetType(editorSession, item.parent);
      const isVisible = _.get(item, `properties.${breakpoint}.visible`, false);
    
      return isVisible && parentType !== HOTSPOT && isWidgetInVisibleFrame(widgets, item);
    });    
  
    const ordered = _.orderBy(filtered, 'order', 'desc'); // 'asc' or 'desc'
  
    return ordered.map(item => ({
      value: item.uid,
      label: item.name,
      type: 'carousel',
      carousel: {
        name: item.name,
        pageId: item.pageId,
        parent: item.parent,
        uid: item.uid,
      },
    }));
  })();

  useEffect(() => {
    try {
      const carouselUid = _.get(value, "value.carouselUid");
      const isCarouselExist = items.find(item => item.value === carouselUid);

      if((!isCarouselExist && carouselUid) || value?.value === null) {
        const action = _.omit(value, 'value');
        onChange({
          ...action,
          deleted: true
        });
        return;
      }
  
      if (!carouselUid) {
        setSelectedCarouselId(null);
        return;
      }
  
      setSelectedCarouselId((prev) => {
        if(carouselUid === prev) return prev;
        return carouselUid;
      });
    } catch {}
  }, [value, items]);

  const handleChange = (e) => {
    if (e.value === selectedCarouselId) return;

    const action = {
      ...value,
      value: {
        name: "",
        slideUid: "",
        carouselName: e.label || e.name || "",
        carouselUid: e.value || "",
      },
    };

    onChange(action);
  };

  return (
    <div className={styles.pageHyperLink}>
      <Property name="Carousel" className={styles.pageHyperLinkProperty}>
        <Property.Dropdown
          searchable={true}
          className={styles.pageHyperLinkDropOption}
          options={items}
          components={{
            Option: CarouselLinkDropdown,
            SingleValue: CustomSingleValue
          }}
          value={selectedCarouselId}
          onChange={handleChange}
          error={saveError && !selectedCarouselId ? saveError : null}
        />
      </Property>
      <Property name="Slide" className={styles.pageHyperLinkProperty}>
        <CarouselLinkSlide
          value={value}
          editorSession={editorSession}
          onChange={onChange}
          saveError={saveError}
          widgets={widgets}
          selectedCarouselId={selectedCarouselId}
        />
      </Property>
    </div>
  );
};

const PageAnchorAction = ({ value, onChange }) => {
  const top = _.get(value, 'value', true);

  useEffect(() => {
    if (value && !_.has(value, 'value')) {
      handleAnchorChange(top);
    }
  }, [value])

  const handleAnchorChange = (anchor) => {
    const action = {
      ...value,
      value: anchor
    }

    onChange(action);
  }

  return (
    <div className={styles.pageAnchorAction}>
      <Property.RadioButton className={styles.targetRadio}
        title='Top'
        value={top}
        onClick={() => handleAnchorChange(true)}
      />
      <Property.RadioButton className={styles.targetRadio}
        title='Bottom'
        value={!top}
        onClick={() => handleAnchorChange(false)}
      />
    </div>
  )
}

const AnchorDropdownOption = (props) => {
  return (
    <components.Option {...props} className={styles.anchorOption}>
      {React.createElement(props.data.value, {}, [props.children])}
    </components.Option>
  );
}

const AnchorAction = ({ editorSession, value, onChange, saveError, optionsData }) => {
  const anchors = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`);
    return widgets.filter(item => item.type === ANCHOR && _.get(item, 'properties.desktop.visible') && isWidgetInVisibleFrame(widgets, item))
      .map(item => {
        return {
          value: item.uid,
          label: item.name,
          type: item.type
        }
      });
  });

  const anchorTag = _.get(value, 'value', '');

  const handleChange = (e) => {
    var anchorValue = e.value;
    const action = {
      ...value,
      value: anchorValue
    }
    onChange(action);
  }

  return (
    <Property name='Navigate to anchor in the page' className={styles.anchorProperty}>
      <Property.Dropdown
        searchable={false}
        className={styles.anchorDropOption}
        options={anchors}
        components={{
          Option: AnchorDropdownOption
        }}
        value={anchorTag}
        onChange={handleChange}
        error={saveError} />
    </Property>
  )
}
const MultipageAnchorDropdownOption = (props) => {
  return (
    <components.Option {...props} className={styles.anchorOption}>
      {props.data?.label || ""}
    </components.Option>
  );
}

const anchorOptionKey = (pageId, uid) => `${pageId || ''}::${uid || ''}`;

const MultipageAnchorAction = ({ state, onChange, saveError, anchorWidgets = {}, editorSession }) => {
  const { items: { widgets: fetchedAnchors = [] } = {}, loading = false } = anchorWidgets;
  const { id } = useParams();
  const editorWidgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`) || []);
  const pagesData = useSelector(state => _.get(state, 'pages.page', {}));

  const draftAnchors = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`);
    return widgets.filter(item => item.type === ANCHOR && _.get(item, 'properties.desktop.visible') && isWidgetInVisibleFrame(widgets, item))
      .map(item => {
        return {
          value: anchorOptionKey(id, item.uid),
          label: item.name,
          type: item.type,
          uid: item.uid,
          pageId: id
        }
      });
  });



  // backend data need to be corrected
  const options = fetchedAnchors.filter(item => {
    if (item.type !== ANCHOR || !_.get(item, "properties.desktop.visible")) return false;
    const widgets = item.pageId === id ? editorWidgets : (_.get(pagesData, `${item.pageId}.item.draft.widgets`, []) || _.get(pagesData, `${item.pageId}.item.widgets`, []));
    return isWidgetInVisibleFrame(widgets, item);
  })
  .map(({ name, uid, pageId }) => {
    return {
      value: anchorOptionKey(pageId, uid),
      label: name,
      uid,
      pageId,
    };
  });
  // anchors are unique per page, so key options by pageId + uid — the same uid can
  // legitimately exist on more than one page and each must stay selectable
  const bothDraftAndSavedAnchors = _.uniqBy([...draftAnchors, ...options], "value");

  const handleChange = ({ value }) => {
    const selectedAnchor = bothDraftAndSavedAnchors.find(item => item.value === value)
    if (!selectedAnchor) return;

    const action = _.pick(selectedAnchor, "uid", "pageId")
    onChange({...state, value: action });
  }

  const anchorValue = _.get(state, "value.uid")
    ? anchorOptionKey(_.get(state, "value.pageId", id), _.get(state, "value.uid"))
    : ""

  return (
    <Property name='Navigate to anchor in the page' className={styles.anchorProperty}>
      <Property.Dropdown
        searchable={false}
        className={styles.anchorDropOption}
        options={bothDraftAndSavedAnchors}
        components={{ Option: MultipageAnchorDropdownOption, LoadingIndicator: null }}
        onChange={handleChange}
        error={saveError}
        value={anchorValue}
        isLoading={loading}
      />
    </Property>
  )
}

const ContentCard = ({ assetMapping, selected, onCancel }) => {
  const [tooltip, setTooltip] = useState(false)
  const defaultContentImage = '/public/cleverstory/v2/static/default_card.jpg';

  const icon = _.get(assetMapping, 'icon.key', null) || _.get(assetMapping, 'icon.url', null);
  const name = _.get(assetMapping, 'name', null);
  const metaData = _.get(assetMapping, 'metadata', {})
  const fileMetaData = FileService.fileMetaData(metaData);

  const storyboardType = _.get(assetMapping, 'storyboardType');
  const entityType = _.get(assetMapping, 'entityType');

  const renderStoryboardType = (type) => {
    if (type === 'LANDING_PAGE') {
      return 'Landing page';
    } else if (INTERACTIVE_CONTENT_TYPES.includes(type)) {
      return 'Interactive content';
    } else if (type === 'RESOURCE_CENTER') {
      return 'Resource center';
    } else if (type === 'SPATIAL') {
      return 'Spatial';
    } else {
      return 'Flipbook';
    }
  }

  const handleTooltip = (isTruncated) => {
    setTooltip(isTruncated)
  }

  return (
    <div className={cx(styles.contentCardWrapper, { [styles.selected]: selected })} onWheel={() => setTooltip(false)}>
      {selected &&
        <button className={styles.closeButton} onClick={onCancel}>
          <i className={cx('cleverstory-close')} />
        </button>
      }
      <div className={styles.iconWrapper}>
        <IKImage className={styles.icon} path={icon || defaultContentImage} />
      </div>
      <div className={styles.detailsContainer}>
        <Tooltip placement="auto" content={name} disabled={!tooltip} container={document.body}>
          <span className={styles.name}>
            <Truncate lines={1} width={110} ellipsis="..." onTruncate={handleTooltip}>
              {name}
            </Truncate>
          </span>
        </Tooltip>
        <div className={styles.metaData}>
          <div className={styles.details}>
            <div className={styles.type}>
              {entityType === 'asset' &&
                <>
                  <i className={cx(styles.fileIcon, `${FileService.fileIcon(metaData)}`)} />
                  <span className={styles.textFormat} >{FileService.fileType(metaData)}</span>
                </>
              }
              {entityType === 'page' &&
                <>
                  <i className={cx(styles.fileIcon, `${FileService.storyboardIcon(storyboardType)}`)} />
                  <span className={styles.storyboardTextFormat} >{renderStoryboardType(storyboardType)}</span>
                </>
              }
            </div>
            {fileMetaData && <i className={styles.seperator}></i>}
            {fileMetaData && <span className={styles.contentMetadata}>{fileMetaData}</span>}
          </div>
          <span className={styles.date}>{dateformat(metaData.modified_date, 'dd mmm yyyy')}</span>
        </div>
      </div>
    </div>
  )
}

const AssetDropdownOption = (props) => {
  const assetMapping = _.get(props, 'data.assetMapping.asset');
  return (
    <components.Option {...props} className={styles.assetOption}>
      <ContentCard assetMapping={assetMapping} />
    </components.Option>
  );
}

const AssetAction = ({ value, onChange, saveError }) => {
  const dispatch = useDispatch();

  const storyboard = useSelector((state) => _.get(state, `storyboards.storyboard.item`, {}));
  const [query, setQuery] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const loading = useSelector((state) => _.get(state, `content.loading`, false));

  const contents = useSelector(state => {
    const assets = _.get(state, 'content.assets');

    return assets.filter(item => item.entityType === "asset" || item.entityType === "page")
      .map(item => {
        return {
          value: item.id,
          label: item.asset.name,
          type: item.entityType,
          assetMapping: item
        }
      })
  })

  const handleQueryChange = (value) => {
    setQuery(value);
  };

  const handleDropdownChange = (e) => {
    const action = {
      ...value,
      value: e.value,
      assetData: e.assetMapping,
      newTab: false
    }
    onChange(action);
  }

  const reset = () => {
    const action = {
      ...value,
      value: null,
      assetData: null,
      newTab: false
    }
    onChange(action);
  }

  const getAssetMappings = (query) => {
    if (query === "") {
      var request = {
        storyboard: _.get(storyboard, 'id'),
        order: "DESC",
        sort: "modified_date"
      }
    }
    else {
      var request = {
        query: query,
        storyboard: _.get(storyboard, 'id')
      }
    }
    dispatch(fetchAssetMappings(request, 0, 20));
  }

  useEffect(() => {
    if (query && _.size(query) > 0) {
      getAssetMappings(query);
      return
    }

    dispatch(invalidateAssetMappings());
  }, [storyboard, query]);

  useEffect(() => {
    getAssetMappings("");
  }, [query]);

  useEffect(() => {
    const assetData = _.get(value, 'value.content.asset') || _.get(value, 'assetData.asset');

    if (!assetData) {
      setSelectedAsset(null);
      return
    }

    setSelectedAsset(assetData);
  }, [value])

  return (
    <>
      {selectedAsset ?
        <ContentCard assetMapping={selectedAsset} selected={selectedAsset} onCancel={reset} />
        :
        <Property className={styles.assetProperty}>
          <i className={cx(styles.searchIcon, 'cleverstory-search')} />
          <Property.Dropdown
            searchable={true}
            placeholder='Type to search'
            className={styles.assetDropOption}
            options={contents}
            components={{
              Option: AssetDropdownOption
            }}
            onChange={handleDropdownChange}
            input={query}
            inputChange={handleQueryChange}
            isLoading={loading}
            error={saveError} />
        </Property>
      }
    </>
  )
}

const PageCard = ({ page, selected, onCancel }) => {
  const [tooltip, setTooltip] = useState(false)
  const defaultContentImage = '/public/cleverstory/v2/static/default_card.jpg';

  const icon = _.get(page, 'icon.key', null) || _.get(page, 'icon.url', null);
  const name = _.get(page, 'name', null);

  const storyboardType = _.get(page, 'storyboard.type');

  const renderStoryboardType = (type) => {
    if (type === 'LANDING_PAGE') {
      return 'Landing page';
    } else if (INTERACTIVE_CONTENT_TYPES.includes(type)) {
      return 'Interactive content';
    } else if (type === 'RESOURCE_CENTER') {
      return 'Resource center';
    }
  }

  const handleTooltip = (isTruncated) => {
    setTooltip(isTruncated)
  }

  return (
    <div className={cx(styles.contentCardWrapper, { [styles.selected]: selected })} onWheel={() => setTooltip(false)}>
      {selected &&
        <button className={styles.closeButton} onClick={onCancel}>
          <i className={cx('cleverstory-close')} />
        </button>
      }
      <div className={styles.iconWrapper}>
        <IKImage className={styles.icon} path={icon || defaultContentImage} />
      </div>
      <div className={styles.detailsContainer}>
        <Tooltip placement="auto" content={name} disabled={!tooltip} container={document.body}>
          <span className={styles.name}>
            <Truncate lines={1} width={110} ellipsis="..." onTruncate={handleTooltip}>
              {name}
            </Truncate>
          </span>
        </Tooltip>
        <div className={styles.metaData}>
          <div className={styles.details}>
            <div className={styles.type}>
              <i className={cx(styles.fileIcon, `${FileService.storyboardIcon(storyboardType)}`)} />
              <span className={styles.storyboardTextFormat} >{renderStoryboardType(storyboardType)}</span>
            </div>
          </div>
          <span className={styles.date}>{dateformat(page.modifiedDate, 'dd mmm yyyy')}</span>
        </div>
      </div>
    </div>
  )
}

const PageDropdownOption = (props) => {
  const page = _.get(props, 'data.page');
  return (
    <components.Option {...props} className={styles.assetOption}>
      <PageCard page={page} />
    </components.Option>
  );
}

const SlideDropdownOption = (props) => {  
  const slide = _.get(props, 'data.slide');

  return (
    <components.Option {...props} className={cx(styles.assetOption, styles.slideOption)}>
      <SlideCard slide={slide} />
    </components.Option>
  );
}

const PageHyperLinkSlide = ({ value, onChange, saveError, editorSession }) => {
  const [query, setQuery] = useState('');
  const [selectedSlide, setSelectedSlide] = useState(null);
  const pageId = _.get(value, 'value.page') || null;
  const { id } = useParams();

  const loading = useSelector((state) => { 
    if(id === pageId){
      return false;
    }
    return _.get(state, `pages.page.${pageId}.loading`)
  });

  const dispatch = useDispatch();

  const widgets = useSelector((state) =>
    id === pageId
      ? _.get(state, `${editorSession}.present.widgets`, [])
      : _.get(state, `pages.page.${pageId}.item.draft.widgets`, [])
  );

  const slideWidgets = useMemo(() => {
    if (!Array.isArray(widgets)) {
      return [];
    }

    return _.chain(widgets)
      .filter(
        (item) =>
          item.type === SLIDE &&
          _.get(item, "properties.desktop.visible", false) &&
          item.id && // Exclude the current page
          (query ? _.includes(_.toLower(item.name), _.toLower(query)) : true) // Search filter
      )
      .sortBy("order") // Sort by 'order' key
      .map((slide) => ({
          value: slide.id || "", // Use meaningful value (e.g., slide ID)
          label: slide.name || "", // Use a name if available
          type: slide.type || "", // Keep type if needed
          uid: slide.uid || "",
          slide
      }))
      .value();
  }, [widgets, query]);

  useEffect(() => {
    if(!slideWidgets?.length && pageId && id !== pageId && !loading) {
      dispatch(fetchPage(pageId));
    }
  }, [slideWidgets, pageId, id, loading]);
  
  useEffect(() => {
    if(!slideWidgets?.length) {
      setSelectedSlide(null);
      return;
    }

    const uid = _.get(value, 'value.uid');
    const slideId = _.get(value, 'value.slide');
    const slideName = _.get(value, 'value.name');
    const slideIcon = _.get(value, 'value.icon.url');

    const slide = slideWidgets.find(item => item.uid === uid);

    if(!slide && uid) {
      const action = _.omit(value, 'value');
      onChange({
        ...action,
        deleted: true
      });
      return;
    }

    if (!slideId || !slideName) {
      setSelectedSlide(null);
      return;
    }

    setSelectedSlide({
      name: slide?.label || slideName,
      id: slideId,
      icon: {
        url: slideIcon
      }
    });
  }, [value, slideWidgets]);

  const handleQueryChange = (value) => {
    setQuery(value);
  };

  const handleDropdownChange = (e) => {
    const action = {
      ...value,
      value: {
        ...value.value,
        slide: e.value,
        uid: e.slide.uid,
        name: e.slide.name,
        icon: {
          url: e.slide?.icon?.url || e.slide?.icon?.key
        }
      }
    }

    onChange(action);
  }

  const reset = () => {
    const action = {
      ...value,
      value: {
        ...value.value,
        slide: "",
        uid: "",
        name: "",
        icon: null
      }
    }

    onChange(action);
  } 

  return (
    <div className={styles.pageAction}>
      {selectedSlide ?
        <SlideCard slide={selectedSlide} selected={selectedSlide} onCancel={reset} />
        :
        <Property className={cx(styles.assetProperty, styles.slideProperty)}>
          <i className={cx(styles.searchIcon, 'cleverstory-search')} />
          <Property.Dropdown
            searchable={true}
            placeholder='Type to search'
            className={styles.assetDropOption}
            options={slideWidgets}
            components={{
              Option: SlideDropdownOption
            }}
            isLoading={loading}
            open
            onChange={handleDropdownChange}
            input={query}
            inputChange={handleQueryChange}
            error={saveError} />
        </Property>
      }
    </div>
  )
}

const CarouselLinkSlide = ({ value, onChange, saveError, widgets, selectedCarouselId }) => {
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [query, setQuery] = useState('');

  const slides = useMemo(() => {
    if (!selectedCarouselId || !Array.isArray(widgets)) return [];
  
    const filtered = widgets.filter(
      (item) =>
        item.parent === selectedCarouselId &&
        _.get(item, "properties.desktop.visible", false) &&
        (query ? _.includes(_.toLower(item.name), _.toLower(query)) : true) // Search filter
    );
    const ordered = _.orderBy(filtered, 'order', 'asc');
  
    return ordered.map(item => ({
      slideUid: item.uid,
      value: item.uid || "",
      label: item.name || "",
      type: item.type || "",
      slide: {
        name: item.name,
        pageId: item.pageId,
        parent: item.parent,
        uid: item.uid,
      },
    }));
  }, [widgets, query, selectedCarouselId]);

  useEffect(() => {
    if(!slides?.length) {
      setSelectedSlide(null);
      return;
    }

    const slideUid = _.get(value, 'value.slideUid');
    const slideName = _.get(value, 'value.name');

    const slide = slides.find(item => item.value === slideUid);

    if(!slide && slideUid && !query) {
      const action = _.omit(value, 'value');
      onChange({
        ...action,
        deleted: true
      });
      return;
    }

    if (!slideUid || !slideName) {
      setSelectedSlide(null);
      return;
    }

    setSelectedSlide({
      name: slide?.label || slideName,
      id: slideUid,
    });
  }, [value, slides, query]);

  const handleDropdownChange = (e) => {
    const action = {
      ...value,
      value: {
        ...value.value,
        slideUid: e.value,
        name: e.slide.name,
      }
    }

    onChange(action);
  }

  return (
    <div className={styles.pageAction}>
      <Property className={cx(styles.assetProperty, styles.slideProperty)}>
        <Property.Dropdown
          searchable={true}
          className={styles.pageHyperLinkDropOption}
          options={slides}
          components={{
            Option: CarouselLinkDropdown,
            SingleValue: CustomSingleValue
          }}
          open
          input={query}
          inputChange={(value) => setQuery(value)}
          value={selectedSlide?.id || null}
          onChange={handleDropdownChange}
          error={saveError}
        />
      </Property>
    </div>
  )
}

const SlideCard = ({
  slide,
  selected,
  onCancel
}) => {
  const [tooltip, setTooltip] = useState(false)
  const defaultContentImage = '/public/cleverstory/v2/static/default_card.jpg';

  const icon = _.get(slide, 'icon.key') || _.get(slide, 'icon.url') || null;
  const name = _.get(slide, 'name', null);

  const handleTooltip = (isTruncated) => {
    setTooltip(isTruncated)
  }

  return (
    selected ? 
    <div className={styles.selectedSlideCardWrapper}>
      <button className={styles.closeButton} onClick={onCancel}>
        <i className={cx('cleverstory-close')} />
      </button>
      <div className={styles.selectedSlideBlock}>
        <div className={styles.slideIconWrapper}>
          <IKImage className={styles.icon} path={icon || defaultContentImage} />
        </div>
        <div className={styles.slideName}>
          <Tooltip placement="auto" content={name} disabled={!tooltip} container={document.body}>
            <span className={styles.name}>
              <Truncate lines={2} width={200} ellipsis="..." onTruncate={handleTooltip}>
                {name}
              </Truncate>
            </span>
          </Tooltip>
        </div>
      </div>
    </div> :
    <div aria-label='asasas' className={styles.slideCardWrapper} onWheel={() => setTooltip(false)}>
      <div className={styles.slideIconWrapper}>
        <IKImage className={styles.icon} path={icon || defaultContentImage} />
      </div>
      <div className={styles.slideName}>
        <Tooltip placement="auto" content={name} disabled={!tooltip} container={document.body}>
          <span className={styles.name}>
            <Truncate lines={2} width={90} ellipsis="..." onTruncate={handleTooltip}>
              {name}
            </Truncate>
          </span>
        </Tooltip>
      </div>
    </div>
  )
}

const getLinkedPageIdFromAction = (action) => {
  if (!action || action.type !== 'page') {
    return null;
  }

  const pageRef = _.get(action, 'page.id');
  if (pageRef) {
    return String(pageRef);
  }

  const rawValue = _.get(action, 'value');
  if (rawValue == null || rawValue === '') {
    return null;
  }

  if (typeof rawValue === 'object' && rawValue.id) {
    return String(rawValue.id);
  }

  return String(rawValue);
};

const mergePageRecordsForDisplay = (candidates) => {
  const pages = candidates.filter((page) => page && page.id);
  if (!pages.length) {
    return null;
  }

  const withIcon = pages.find(
    (page) => _.get(page, 'icon.url') || _.get(page, 'icon.key'),
  );
  const withName = pages.find((page) => page.name);

  if (withIcon) {
    const base = withName || withIcon;
    return {
      ...base,
      ...withIcon,
      icon: withIcon.icon || base.icon,
      name: base.name || withIcon.name,
    };
  }

  return withName || pages[0];
};

const PageAction = ({ value, onChange, saveError }) => {
  const dispatch = useDispatch();

  const storyboard = useSelector((state) => _.get(state, `storyboards.storyboard.item`, {}));
  const [query, setQuery] = useState('');
  const [selectedPage, setSelectedPage] = useState(null);

  const linkedPageId = useMemo(
    () => getLinkedPageIdFromAction(value),
    [value],
  );

  const storyboardPageList = useSelector(
    (state) => _.get(state, 'pages.items', []) || [],
  );

  const fetchedPageDetail = useSelector((state) =>
    linkedPageId ? _.get(state, `pages.page.${linkedPageId}.item`) : null,
  );

  const pageDetailLoading = useSelector((state) =>
    linkedPageId ? _.get(state, `pages.page.${linkedPageId}.loading`, false) : false,
  );

  const loading = useSelector((state) => _.get(state, `pages.pageQuery.loading`, false));
  const newTab = _.get(value, 'newTab', true);
  const pages = useSelector(state => {
    const pages = _.get(state, 'pages.pageQuery.items', []);
    return pages.map(item => {
      return {
        value: item.id,
        label: item.name,
        type: "page",
        page: item
      }
    })
  });

  useEffect(() => {
    if (query && _.size(query) > 0) {
      fetchPages(query);
      return
    }

    dispatch(invalidatePageQuery());
  }, [storyboard, query]);

  useEffect(() => {
    if (!query) {
      fetchPages("");
      return;
    }
  }, [query]);

  useEffect(() => {
    fetchPages('');
  }, []);

  useEffect(() => {
    if (!linkedPageId || value?.type !== 'page') {
      return;
    }

    if (!fetchedPageDetail?.id && !pageDetailLoading) {
      dispatch(fetchPage(linkedPageId, true));
    }
  }, [linkedPageId, value?.type, fetchedPageDetail?.id, pageDetailLoading, dispatch]);

  useEffect(() => {
    if (value?.type !== 'page') {
      setSelectedPage(null);
      return;
    }

    if (!linkedPageId) {
      setSelectedPage(null);
      return;
    }

    const rawValue = _.get(value, 'value');
    const resolved = mergePageRecordsForDisplay([
      _.get(value, 'page'),
      typeof rawValue === 'object' ? rawValue : null,
      storyboardPageList.find((page) => String(page.id) === linkedPageId),
      pages.find((item) => String(item.value) === linkedPageId)?.page,
      fetchedPageDetail,
    ]);

    setSelectedPage(resolved);
  }, [value, pages, storyboardPageList, fetchedPageDetail, linkedPageId]);

  const handleQueryChange = (value) => {
    setQuery(value);
  };

  const handleDropdownChange = (e) => {
    const action = {
      ...value,
      value: e.value,
      page: e.page,
    }
    onChange(action);
  }

  const reset = () => {
    const action = {
      ...value,
      value: null,
      page: null,
      newTab: true
    }
    onChange(action);
  }

  const handleTargetChange = (target) => {
    const action = {
      ...value,
      newTab: target
    }

    onChange(action);
  }

  const fetchPages = (query) => {
    const request = {
      storyboard: _.get(storyboard, 'id'),
      query: query
    }

    dispatch(queryPages(request, 0, 50))
  }

  return (
    <div className={styles.pageAction}>
      {selectedPage ?
        <PageCard page={selectedPage} selected={selectedPage} onCancel={reset} />
        :
        <Property className={styles.assetProperty}>
          <i className={cx(styles.searchIcon, 'cleverstory-search')} />
          <Property.Dropdown
            searchable={true}
            placeholder='Type to search'
            className={styles.assetDropOption}
            options={pages}
            components={{
              Option: PageDropdownOption
            }}
            onChange={handleDropdownChange}
            input={query}
            inputChange={handleQueryChange}
            isLoading={loading}
            error={saveError} />
        </Property>
      }
      <Property name="Open in" className={styles.targetProperty}>
        <div className={styles.body}>
          <Property.RadioButton className={styles.targetRadio}
            title='Current'
            value={!newTab}
            onClick={() => handleTargetChange(false)}
          />
          <Property.RadioButton className={styles.targetRadio}
            title='New Window'
            value={newTab}
            onClick={() => handleTargetChange(true)}
          />
        </div>
      </Property>
    </div>
  )
}

const GatingAction = ({ value, onChange, saveError }) => {

  const gatings = useSelector(state =>
    (_.get(state, "gatings.items", [])).filter(
      item => item?.progressive !== true
    )
  );

  const loading = useSelector(state => _.get(state, 'gatings.published.loading', false));
  const pagination = useSelector(state => _.get(state, 'gatings.published.pagination', {}));
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  const actionValue = _.get(value, 'value', '');
  const activeGating = actionValue && gatings.find(item => item.id === actionValue.gating);

  const renderScreenOption = (gating) => {
    return (
      <>
        <span className={styles.optionName}>
          <TruncatedTextWithTooltip content={gating.name} maxWidth={150} placement='top' />
        </span>
      </>
    )
  }

  const handleGatingScreen = (data) => {
    const gatingRequest = {
      uid: shortid(),
      name: data.name,
      gating: data.id,
      optional: false
    }

    const action = {
      ...value,
      value: gatingRequest
    }

    onChange(action);
  }

  return (
    <div className={styles.gatingAction}>
      <Dropdown className={styles.gatingDropdown}>
        <Dropdown.Toggle>
          {activeGating &&
            <span className={styles.selectedName}>
              <TruncatedTextWithTooltip content={activeGating.name} maxWidth={150} placement='top' />
            </span>
          }
          {!activeGating &&
            <span className={styles.placeholder}>Select Gating</span>
          }
        </Dropdown.Toggle>

        {gatings.length == 0 &&
          <Dropdown.Menu className={styles.emptyOption}>
            <Dropdown.Item>No Options</Dropdown.Item>
          </Dropdown.Menu>
        }

        {gatings.length > 0 &&
          <Dropdown.Menu onError={saveError}>
            <InfiniteScroll
              className={cx(styles.scrollable)}
              loading={loading}
              hasMore={hasMoreItems}
            >
              {gatings.filter(data => data.published).map((data, index) => {
                return (
                  <Dropdown.Item onClick={() => handleGatingScreen(data)} key={index}>
                    {renderScreenOption(data)}
                  </Dropdown.Item>
                );
              })
              }
            </InfiniteScroll>
          </Dropdown.Menu>
        }

      </Dropdown>
      {saveError &&
        <p className={styles.error}>{saveError}</p>
      }
    </div>
  )
}

export { LinkAction, PageAction, PageSpatialHyperLink, ActionHeader };
export default Actions;

{/*
  In IC V1, widgetAnchor was stored as a string.
  For IC V2, it needs to be an object, so we parse and convert the string on the client side.
*/}
const normalizePageActionForEditor = (action) => {
  if (!action || action.type !== 'page') {
    return action;
  }

  const rawValue = _.get(action, 'value');
  if (rawValue != null && typeof rawValue === 'object' && rawValue.id) {
    return {
      ...action,
      value: rawValue.id,
      page: _.get(action, 'page') || rawValue,
    };
  }

  return action;
};

const getValue = (data, storyboardType) => {
  if (storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2 && data?.type === "widgetAnchor") {
    const { value: action } = data; 
    if (typeof action === "string" && action.length > 0) {
      const parsedAction = action
        .slice(1, -1)
        .split(", ")
        .reduce((acc, pair) => {
          const [key, value] = pair.split("=");
          acc[key] = value;
          return acc;
        }, {});

      data.value = parsedAction
      return normalizePageActionForEditor(data)
    }
    return normalizePageActionForEditor(data)
  }
  return normalizePageActionForEditor(data)
}

function isWidgetInVisibleFrame(widgets, anchor) {
  let widget = widgets?.find(item => item.uid === anchor.uid) || anchor;

  while (widget?.parent) {
    const parent = widgets?.find(item => item.uid === widget.parent);
    if (!parent) break;
    if (parent.type === PAGE) {
      return _.get(parent, 'properties.desktop.visible', true) !== false;
    }
    widget = parent;
  }

  return true;
}

function getWidgetsWithAncestor(widgets, parentUid) {
  if (!Array.isArray(widgets) || widgets.length === 0) return [];

  let result = [];

  // Get direct children (including HOTSPOT ones — we’ll decide what to do with them)
  const children = widgets.filter(widget => widget.parent === parentUid);

  children.forEach(child => {
    if (child.type === HOTSPOT) {
      return;
    }

    // Include the child itself
    result.push(child);

    // Recursively get its descendants (which will also skip HOTSPOT subtrees)
    result.push(...getWidgetsWithAncestor(widgets, child.uid));
  });

  return result;
}
